import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MagnifyingGlass, ArrowClockwise, CaretDown, CaretUp,
  Sparkle, Crosshair, Calendar, TrendUp, Clock, Wallet,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `₹${n}`; }
};
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch { return s; }
};
const fmtDays = (n) => {
  if (n === null || n === undefined) return "—";
  if (n === 0) return "today";
  if (n < 0) return `${Math.abs(n)}d overdue`;
  return `in ${n}d`;
};
const cadenceColour = (lbl) => ({
  "monthly":        "bg-emerald-900/40 text-emerald-200 border-emerald-700/40",
  "bi-weekly":      "bg-teal-900/40 text-teal-200 border-teal-700/40",
  "weekly":         "bg-cyan-900/40 text-cyan-200 border-cyan-700/40",
  "very frequent":  "bg-blue-900/40 text-blue-200 border-blue-700/40",
  "bi-monthly":     "bg-amber-900/40 text-amber-200 border-amber-700/40",
  "quarterly":      "bg-orange-900/40 text-orange-200 border-orange-700/40",
  "irregular":      "bg-slate-800 text-slate-300 border-slate-700",
  "single payment": "bg-slate-800 text-slate-400 border-slate-700",
  "no history":     "bg-slate-800 text-slate-500 border-slate-700",
}[lbl] || "bg-slate-800 text-slate-300 border-slate-700");

const SORT_COLS = [
  { id: "vendor",              label: "Party" },
  { id: "outstanding",         label: "Outstanding" },
  { id: "last_payment_date",   label: "Last Paid" },
  { id: "predicted_next_date", label: "Next Predicted" },
  { id: "days_to_next",        label: "Days Out" },
  { id: "predicted_amount",    label: "Predicted Amt" },
  { id: "cadence",             label: "Cadence" },
  { id: "confidence",          label: "Confidence" },
];

export default function PredictionTab({ refreshKey }) {
  const [loading, setLoading]   = useState(true);
  const [data, setData]         = useState({ summary: {}, rows: [], tasks: [] });
  const [query, setQuery]       = useState("");
  const [sortBy, setSortBy]     = useState("days_to_next");
  const [sortDir, setSortDir]   = useState("asc");
  const [expanded, setExpanded] = useState(new Set());
  const [aiBusy, setAiBusy]     = useState(new Set());
  const [aiResults, setAiResults] = useState({});  // vendor -> insight obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/predictions");
      setData(r.data || { summary: {}, rows: [], tasks: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load predictions");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const onSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir(col === "vendor" ? "asc" : "desc"); }
  };
  const toggleExpand = (key) => {
    setExpanded((s) => {
      const ns = new Set(s);
      if (ns.has(key)) ns.delete(key); else ns.add(key);
      return ns;
    });
  };

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = data.rows || [];
    if (q) rows = rows.filter((r) => (r.vendor || "").toLowerCase().includes(q));
    const dir = sortDir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === "cadence")    { va = a.cadence?.cadence_days ?? 9999; vb = b.cadence?.cadence_days ?? 9999; }
      if (sortBy === "confidence") { va = a.cadence?.confidence_pct ?? 0;   vb = b.cadence?.confidence_pct ?? 0;   }
      if (va === null || va === undefined) va = (typeof vb === "number") ? Infinity : "";
      if (vb === null || vb === undefined) vb = (typeof va === "number") ? Infinity : "";
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return rows;
  }, [data.rows, query, sortBy, sortDir]);

  const askAi = async (row) => {
    setAiBusy((s) => new Set(s).add(row.vendor));
    try {
      const r = await api.post("/predictions/ai-insight", {
        vendor:           row.vendor,
        outstanding:      row.outstanding,
        payments:         row.payments_recent,
        cadence:          row.cadence,
        intervals_stats:  row.intervals_stats,
      });
      setAiResults((m) => ({ ...m, [row.vendor]: r.data }));
      setExpanded((s) => new Set(s).add(row.vendor));  // open row to reveal
      toast.success("AI insight generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI insight failed");
    } finally {
      setAiBusy((s) => { const n = new Set(s); n.delete(row.vendor); return n; });
    }
  };

  const downloadCsv = () => {
    const headers = ["Party", "Outstanding", "Last Paid", "Last Amount", "Cadence", "Cadence Days", "Confidence %", "Next Predicted Date", "Days Out", "Predicted Amount"];
    const lines = [headers.join(",")];
    filteredSorted.forEach((r) => {
      const cells = [
        `"${(r.vendor || "").replace(/"/g, '""')}"`,
        r.outstanding || 0,
        r.last_payment_date || "",
        r.last_payment_amount || 0,
        r.cadence?.label || "",
        r.cadence?.cadence_days || "",
        r.cadence?.confidence_pct || 0,
        r.predicted_next_date || "",
        r.days_to_next ?? "",
        r.predicted_amount || 0,
      ];
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Predictions - ${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data.summary || {};

  return (
    <div className="space-y-4" data-testid="prediction-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-100 flex items-center gap-2">
            <Crosshair size={16} weight="duotone" className="text-cyan-300" />
            Prediction
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Forecasts the next payment date &amp; amount per vendor from historical bank debits and outstanding balances. Combines per-vendor cadence with active Upcoming Expenses.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}
            className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800 hover:text-cyan-100"
            data-testid="prediction-refresh">
            <ArrowClockwise size={13} weight="bold" className="mr-1" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv}
            disabled={!filteredSorted.length}
            className="h-9 border-emerald-700 text-emerald-200 bg-slate-900 hover:bg-emerald-900/30 hover:text-emerald-100"
            data-testid="prediction-download">
            CSV
          </Button>
        </div>
      </div>

      {/* Forecast cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <ForecastCard label="Outstanding (total)" icon={<Wallet size={14} weight="duotone" />}
          value={fmtINR(s.total_outstanding || 0)} sub={`${s.vendor_count || 0} vendors`} accent="cyan" />
        <ForecastCard label="Next 7 days" icon={<Clock size={14} weight="duotone" />}
          value={fmtINR(s.forecast_7d?.total || 0)}
          sub={`${fmtINR(s.forecast_7d?.vendor || 0)} vendors · ${fmtINR(s.forecast_7d?.tasks || 0)} tasks`} accent="amber" />
        <ForecastCard label="Next 15 days" icon={<TrendUp size={14} weight="duotone" />}
          value={fmtINR(s.forecast_15d?.total || 0)}
          sub={`${fmtINR(s.forecast_15d?.vendor || 0)} vendors · ${fmtINR(s.forecast_15d?.tasks || 0)} tasks`} accent="rose" />
        <ForecastCard label="Next 30 days" icon={<Calendar size={14} weight="duotone" />}
          value={fmtINR(s.forecast_30d?.total || 0)}
          sub={`${fmtINR(s.forecast_30d?.vendor || 0)} vendors · ${fmtINR(s.forecast_30d?.tasks || 0)} tasks`} accent="emerald" />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by party name…"
          className="h-9 pl-8 bg-slate-950 border-slate-700 text-sm"
          data-testid="prediction-search" />
      </div>

      {/* Main table */}
      <div className="rounded-md border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="prediction-table">
            <thead className="bg-slate-900 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-wider font-bold text-cyan-200">
                <th className="w-8 px-2 py-2"></th>
                {SORT_COLS.map((c) => (
                  <th key={c.id} onClick={() => onSort(c.id)}
                    className="text-left px-3 py-2 cursor-pointer hover:bg-slate-800 select-none"
                    data-testid={`prediction-sort-${c.id}`}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortBy === c.id && (sortDir === "asc"
                        ? <CaretUp size={10} weight="bold" />
                        : <CaretDown size={10} weight="bold" />)}
                    </span>
                  </th>
                ))}
                <th className="w-24 px-2 py-2 text-right">AI</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={SORT_COLS.length + 2} className="text-center py-12 text-slate-500">Loading…</td></tr>
              )}
              {!loading && filteredSorted.length === 0 && (
                <tr><td colSpan={SORT_COLS.length + 2} className="text-center py-12 text-slate-500">
                  {query ? "No vendors match your search." : "No outstanding balances found. Upload Tally first."}
                </td></tr>
              )}
              {!loading && filteredSorted.map((r) => {
                const key = r.vendor;
                const open = expanded.has(key);
                const overdue = r.days_to_next !== null && r.days_to_next < 0;
                const ai = aiResults[key];
                return (
                  <React.Fragment key={key}>
                    <tr onClick={() => toggleExpand(key)}
                      className={`border-t border-slate-800 cursor-pointer transition-colors ${
                        open ? "bg-cyan-950/30"
                        : overdue ? "bg-rose-950/20 hover:bg-rose-950/30"
                        : "hover:bg-slate-900/60"
                      }`}
                      data-testid={`prediction-row-${key.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}>
                      <td className="px-2 py-2 text-center text-slate-500">
                        {open ? <CaretDown size={12} weight="bold" /> : <CaretUp size={12} weight="bold" className="rotate-90" />}
                      </td>
                      <td className="px-3 py-2 max-w-[260px]">
                        <div className="font-semibold text-slate-100 truncate" title={r.vendor}>{r.vendor}</div>
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider mt-0.5">
                          {r.n_payments} pmts · last {fmtINR(r.last_payment_amount)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-cyan-100 font-bold">{fmtINR(r.outstanding)}</td>
                      <td className="px-3 py-2 text-slate-400 font-mono">{fmtDate(r.last_payment_date)}</td>
                      <td className="px-3 py-2 text-slate-300 font-mono">{fmtDate(r.predicted_next_date)}</td>
                      <td className={`px-3 py-2 font-mono ${overdue ? "text-rose-300 font-semibold" : "text-slate-300"}`}>
                        {fmtDays(r.days_to_next)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-200 font-semibold">{fmtINR(r.predicted_amount)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-wider font-bold ${cadenceColour(r.cadence?.label)}`}>
                          {r.cadence?.label || "—"}
                        </span>
                        <div className="text-[9px] text-slate-500 mt-0.5">
                          {r.cadence?.cadence_days ? `${r.cadence.cadence_days}d avg` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <ConfidenceBar pct={r.cadence?.confidence_pct || 0} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); askAi(r); }}
                          disabled={aiBusy.has(r.vendor)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-violet-700/60 bg-violet-950/40 text-violet-200 text-[10px] uppercase tracking-wider font-bold hover:bg-violet-900/60 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`prediction-ai-${key.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}
                          title="Ask AI to reason over this vendor's payment pattern">
                          <Sparkle size={10} weight={ai ? "fill" : "duotone"} />
                          {aiBusy.has(r.vendor) ? "…" : (ai ? "Refresh" : "AI")}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-950" data-testid={`prediction-expand-${key.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}>
                        <td className="px-3 pb-4 pt-1" colSpan={SORT_COLS.length + 2}>
                          <div className="grid md:grid-cols-2 gap-3">
                            <div className="rounded-sm border border-cyan-900/40 bg-slate-900/60 p-3">
                              <div className="text-[10px] uppercase tracking-wider text-cyan-200 font-bold mb-2">Pattern stats</div>
                              <Stat label="Total paid lifetime" value={fmtINR(r.total_paid_lifetime)} />
                              <Stat label="Avg payment amount" value={fmtINR(r.avg_payment)} />
                              <Stat label="Mean gap (days)"    value={r.intervals_stats?.mean ?? "—"} />
                              <Stat label="Median gap (days)"  value={r.intervals_stats?.median ?? "—"} />
                              <Stat label="Std dev (days)"     value={r.intervals_stats?.stdev ?? "—"} />
                              <Stat label="Deviation"          value={r.cadence?.deviation_pct != null ? `${r.cadence.deviation_pct}%` : "—"} />
                            </div>
                            <div className="rounded-sm border border-violet-900/40 bg-slate-900/60 p-3">
                              <div className="text-[10px] uppercase tracking-wider text-violet-200 font-bold mb-2 flex items-center gap-1">
                                <Sparkle size={11} weight="duotone" /> AI insight
                              </div>
                              {!ai && (
                                <div className="text-[11px] text-slate-500">
                                  Click the AI button on the row to ask Gemini to reason over this vendor&apos;s payment pattern.
                                </div>
                              )}
                              {ai && (
                                <div className="space-y-1.5 text-[11px] text-slate-200">
                                  <Stat label="Next date"   value={fmtDate(ai.next_date)} />
                                  <Stat label="Next amount" value={fmtINR(ai.next_amount)} />
                                  <Stat label="Confidence"  value={(ai.confidence || "—").toUpperCase()} />
                                  <div className="pt-1.5 mt-1.5 border-t border-violet-900/40 text-slate-300 leading-snug">
                                    {ai.rationale || "—"}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="md:col-span-2 rounded-sm border border-slate-700 bg-slate-900/60 overflow-hidden">
                              <div className="px-3 py-1.5 bg-slate-900 border-b border-slate-700 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                                Recent payments (newest first)
                              </div>
                              {(!r.payments_recent || r.payments_recent.length === 0) ? (
                                <div className="px-3 py-3 text-[11px] text-slate-500">No payment history.</div>
                              ) : (
                                <table className="w-full text-[11px]">
                                  <thead>
                                    <tr className="bg-slate-900/60 text-[9px] uppercase tracking-wider text-slate-500">
                                      <th className="text-left px-3 py-1.5">Date</th>
                                      <th className="text-right px-3 py-1.5">Amount</th>
                                      <th className="text-left px-3 py-1.5">Bank</th>
                                      <th className="text-left px-3 py-1.5">Reference / Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.payments_recent.map((p, i) => (
                                      <tr key={i} className="border-t border-slate-800/60">
                                        <td className="px-3 py-1 font-mono text-slate-300">{fmtDate(p.date)}</td>
                                        <td className="px-3 py-1 text-right font-mono tabular-nums text-amber-200">{fmtINR(p.amount)}</td>
                                        <td className="px-3 py-1 text-slate-400">{p.account || "—"}</td>
                                        <td className="px-3 py-1 text-slate-400 font-mono truncate max-w-[420px]" title={p.description}>
                                          {p.ref ? <span className="text-slate-500 mr-1">{p.ref}</span> : null}
                                          {p.description || "—"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upcoming recurring tasks */}
      {data.tasks?.length > 0 && (
        <div className="rounded-md border border-slate-700 overflow-hidden">
          <div className="px-3 py-2 bg-slate-900 border-b border-slate-700 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-200 flex items-center gap-2">
              <Calendar size={12} weight="duotone" /> Upcoming Recurring Expenses
            </div>
            <div className="text-[10px] text-slate-500">{data.tasks.length} active</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="prediction-tasks-table">
              <thead>
                <tr className="bg-slate-900/60 text-[9px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-1.5">Task</th>
                  <th className="text-left px-3 py-1.5">Vendor</th>
                  <th className="text-left px-3 py-1.5">Frequency</th>
                  <th className="text-left px-3 py-1.5">Due Date</th>
                  <th className="text-left px-3 py-1.5">Days Out</th>
                  <th className="text-right px-3 py-1.5">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.tasks.map((t, i) => (
                  <tr key={i} className="border-t border-slate-800/60">
                    <td className="px-3 py-1.5 font-semibold text-slate-200">{t.task}</td>
                    <td className="px-3 py-1.5 text-slate-400">{t.vendor || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-400">{t.frequency || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-300">{fmtDate(t.due_date)}</td>
                    <td className={`px-3 py-1.5 font-mono ${t.days_to_due !== null && t.days_to_due < 0 ? "text-rose-300 font-semibold" : "text-slate-300"}`}>
                      {fmtDays(t.days_to_due)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-amber-200">{fmtINR(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ForecastCard({ label, value, sub, accent = "cyan", icon }) {
  const a = {
    cyan:    "border-cyan-800/40 text-cyan-200",
    amber:   "border-amber-800/40 text-amber-200",
    rose:    "border-rose-800/40 text-rose-200",
    emerald: "border-emerald-800/40 text-emerald-200",
  }[accent];
  return (
    <div className={`rounded-md border bg-slate-900/60 px-3 py-2 ${a}`} data-testid={`prediction-card-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-[0.2em] opacity-70 font-bold flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-base font-bold font-mono">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function ConfidenceBar({ pct }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  const tone = p >= 70 ? "bg-emerald-500" : p >= 45 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="w-20">
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${p}%` }} />
      </div>
      <div className="text-[9px] text-slate-500 mt-0.5 font-mono">{p}%</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200 font-mono tabular-nums">{value}</span>
    </div>
  );
}
